import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Upload, RefreshCw, CheckCircle2, Loader2, X, Plus, Pencil, Trash2, Save, ChevronUp, ChevronDown, ArrowLeftRight,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FlyerGifsUpload = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [inPersonUrl, setInPersonUrl] = useState('');
  const [privateUrl, setPrivateUrl] = useState('');
  const [savingQr, setSavingQr] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [slotsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/flyer-gifs/slots`),
        axios.get(`${API}/api/flyer-gifs/settings`),
      ]);
      setSlots(slotsRes.data.slots || []);
      setInPersonUrl(settingsRes.data.in_person_url || '');
      setPrivateUrl(settingsRes.data.private_url || '');
    } catch (err) {
      toast.error('Could not load slots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveQrUrls = async () => {
    setSavingQr(true);
    try {
      await axios.patch(`${API}/api/flyer-gifs/settings`, {
        in_person_url: inPersonUrl.trim(),
        private_url: privateUrl.trim(),
      });
      toast.success('QR code URLs saved — visit /flyer to see them');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingQr(false);
    }
  };

  const handleUpload = async (slotId, file) => {
    if (!file) return;
    setUploadingId(slotId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/flyer-gifs/upload/${slotId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded (${res.data.size_mb} MB)`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploadingId(null);
    }
  };

  const startEdit = (slot) => {
    setEditingId(slot.id);
    setEditingLabel(slot.label);
  };

  const saveEdit = async () => {
    if (!editingLabel.trim()) return toast.error('Name cannot be empty');
    try {
      await axios.patch(`${API}/api/flyer-gifs/slots/${editingId}`, { label: editingLabel.trim() });
      toast.success('Renamed');
      setEditingId(null);
      setEditingLabel('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rename failed');
    }
  };

  const cancelEdit = () => { setEditingId(null); setEditingLabel(''); };

  const removeSlot = async (slotId) => {
    if (!window.confirm('Delete this dance/class from the flyer?')) return;
    try {
      await axios.delete(`${API}/api/flyer-gifs/slots/${slotId}`);
      toast.success('Removed');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const addSlot = async () => {
    if (!newLabel.trim()) return toast.error('Enter a class name first');
    setAdding(true);
    try {
      await axios.post(`${API}/api/flyer-gifs/slots`, { label: newLabel.trim() });
      toast.success('Added — scroll to see the new slot at the end');
      setNewLabel('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const moveSlot = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= slots.length) return;
    const next = [...slots];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSlots(next);
    try {
      await axios.post(`${API}/api/flyer-gifs/slots/reorder`, {
        ordered_ids: next.map((s) => s.id),
      });
    } catch (err) {
      toast.error('Reorder failed');
      await load();
    }
  };

  const swapWith = async (slotId, targetId) => {
    if (!targetId || targetId === slotId) return;
    const idxA = slots.findIndex((s) => s.id === slotId);
    const idxB = slots.findIndex((s) => s.id === targetId);
    if (idxA < 0 || idxB < 0) return;
    const next = [...slots];
    [next[idxA], next[idxB]] = [next[idxB], next[idxA]];
    setSlots(next);
    try {
      await axios.post(`${API}/api/flyer-gifs/slots/reorder`, {
        ordered_ids: next.map((s) => s.id),
      });
      toast.success(`Swapped positions ${idxA + 1} ↔ ${idxB + 1}`);
    } catch (err) {
      toast.error('Swap failed');
      await load();
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-white/60 hover:text-yellow-400" data-testid="flyer-gifs-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-black">Flyer Slots &amp; GIF Uploads</h1>
              <p className="text-sm text-white/60 max-w-2xl">
                Rename slots, add new dances, delete duplicates, and upload the GIF for each circle on{' '}
                <Link to="/flyer" className="text-yellow-400 underline">/flyer</Link>. The gold{' '}
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500 text-black font-bold text-xs">1</span>{' '}
                badge shows each slot's position on the ring (1 = top, then clockwise). Use{' '}
                <span className="text-yellow-400 font-bold">Swap with…</span> to move a GIF to a different circle instantly. Max 20 MB per file.
              </p>
            </div>
          </div>
          <button
            onClick={load}
            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm flex items-center gap-2"
            data-testid="flyer-gifs-reload"
          >
            <RefreshCw className="w-4 h-4" /> Reload
          </button>
        </div>

        {/* Registration QR code URLs */}
        <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/40 mb-6">
          <p className="text-xs uppercase tracking-widest text-red-300 font-bold mb-1">
            Registration QR Codes
          </p>
          <p className="text-xs text-white/60 mb-3">
            Paste the URLs you want each QR code on <span className="text-yellow-400">/flyer</span> to open. Two QR codes appear at the top corners of the flyer (in-person on the left, private on the right). Leave a field empty to hide that QR code.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-bold text-red-300 uppercase tracking-wider">In-Person Registration URL</label>
              <input
                type="url"
                value={inPersonUrl}
                onChange={(e) => setInPersonUrl(e.target.value)}
                placeholder="https://casasamba.com/register/in-person"
                className="w-full mt-1 px-3 py-2.5 rounded-lg bg-black/40 border border-red-500/30 text-white placeholder-white/30 focus:border-red-400 focus:outline-none text-sm"
                data-testid="flyer-qr-in-person-url"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-red-300 uppercase tracking-wider">Private Class Registration URL</label>
              <input
                type="url"
                value={privateUrl}
                onChange={(e) => setPrivateUrl(e.target.value)}
                placeholder="https://casasamba.com/register/private"
                className="w-full mt-1 px-3 py-2.5 rounded-lg bg-black/40 border border-red-500/30 text-white placeholder-white/30 focus:border-red-400 focus:outline-none text-sm"
                data-testid="flyer-qr-private-url"
              />
            </div>
          </div>
          <button
            onClick={saveQrUrls}
            disabled={savingQr}
            data-testid="flyer-qr-save-btn"
            className="px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-400 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {savingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save QR URLs
          </button>
        </div>

        {/* Add new slot */}
        <div className="p-4 rounded-2xl bg-yellow-500/10 border border-yellow-500/40 mb-6">
          <p className="text-xs uppercase tracking-widest text-yellow-300 font-bold mb-2">
            Add a Dance / Class
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addSlot()}
              placeholder="e.g. Frevo Recife, Maracatu Nation, Kids Capoeira..."
              className="flex-1 px-3 py-2.5 rounded-lg bg-black/40 border border-yellow-500/30 text-white placeholder-white/30 focus:border-yellow-400 focus:outline-none"
              data-testid="flyer-gif-new-label"
            />
            <button
              onClick={addSlot}
              disabled={adding || !newLabel.trim()}
              data-testid="flyer-gif-add-btn"
              className="px-5 py-2.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Slot
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-white/60 py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading...
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {slots.map((slot, idx) => (
              <div
                key={slot.id}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-yellow-400/50 transition-colors relative"
                data-testid={`flyer-gif-slot-${slot.id}`}
              >
                {/* Position badge — top-left corner */}
                <div
                  className="absolute -top-2 -left-2 z-10 w-10 h-10 rounded-full bg-yellow-500 border-4 border-neutral-950 text-black font-black text-sm flex items-center justify-center shadow-lg"
                  data-testid={`flyer-gif-position-${slot.id}`}
                  title={`Position ${idx + 1} on the flyer ring`}
                >
                  {idx + 1}
                </div>

                {/* Label / edit / delete row */}
                <div className="flex items-start justify-between mb-3 gap-2">
                  {editingId === slot.id ? (
                    <input
                      type="text"
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit();
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      autoFocus
                      className="flex-1 min-w-0 px-2 py-1 rounded bg-black/60 border border-yellow-400 text-sm text-yellow-300 font-bold"
                      data-testid={`flyer-gif-label-input-${slot.id}`}
                    />
                  ) : (
                    <p
                      className="text-sm font-bold uppercase tracking-wider text-yellow-400 leading-tight flex-1"
                      data-testid={`flyer-gif-label-${slot.id}`}
                    >
                      {slot.label}
                    </p>
                  )}
                  {slot.url && !editingId && <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />}
                </div>

                {/* Preview */}
                <div className="aspect-square rounded-full overflow-hidden bg-black border-2 border-white/10 mb-3 flex items-center justify-center">
                  {slot.url ? (
                    slot.url.endsWith('.mp4') || slot.url.endsWith('.webm') ? (
                      <video
                        src={`${API}${slot.url}`}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={`${API}${slot.url}`}
                        alt={slot.label}
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <span className="text-white/30 text-xs uppercase tracking-widest">Empty</span>
                  )}
                </div>

                {/* Edit / Save / Cancel */}
                <div className="flex items-center gap-1 mb-2">
                  {editingId === slot.id ? (
                    <>
                      <button
                        onClick={saveEdit}
                        className="flex-1 px-2 py-1.5 rounded-md bg-green-500 hover:bg-green-400 text-black font-bold text-xs flex items-center justify-center gap-1"
                        data-testid={`flyer-gif-save-${slot.id}`}
                      >
                        <Save className="w-3 h-3" /> Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-2 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-xs"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(slot)}
                        className="flex-1 px-2 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-bold flex items-center justify-center gap-1"
                        data-testid={`flyer-gif-rename-${slot.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Rename
                      </button>
                      <button
                        onClick={() => removeSlot(slot.id)}
                        className="px-2 py-1.5 rounded-md bg-red-500/20 hover:bg-red-500/40 text-red-300 text-xs"
                        title="Delete this slot"
                        data-testid={`flyer-gif-delete-${slot.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveSlot(idx, -1)}
                        disabled={idx === 0}
                        className="px-2 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs disabled:opacity-30"
                        title="Move earlier in the ring"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveSlot(idx, +1)}
                        disabled={idx === slots.length - 1}
                        className="px-2 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs disabled:opacity-30"
                        title="Move later in the ring"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>

                {/* Swap with — quick jump to any other position */}
                {editingId !== slot.id && (
                  <div className="flex items-center gap-1 mb-2">
                    <ArrowLeftRight className="w-3.5 h-3.5 text-yellow-400 shrink-0 ml-1" />
                    <select
                      value=""
                      onChange={(e) => swapWith(slot.id, e.target.value)}
                      className="flex-1 px-2 py-1.5 rounded-md bg-black/50 border border-yellow-400/40 text-yellow-300 text-xs font-bold cursor-pointer hover:border-yellow-400 focus:outline-none focus:border-yellow-400"
                      data-testid={`flyer-gif-swap-${slot.id}`}
                    >
                      <option value="">Swap with…</option>
                      {slots.filter((s) => s.id !== slot.id).map((s, i) => {
                        const targetIdx = slots.findIndex((x) => x.id === s.id);
                        return (
                          <option key={s.id} value={s.id}>
                            Position {targetIdx + 1} · {s.label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Upload */}
                <label
                  htmlFor={`upload-${slot.id}`}
                  className="w-full px-3 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-xs flex items-center justify-center gap-2 cursor-pointer"
                >
                  {uploadingId === slot.id ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" /> {slot.url ? 'Replace GIF' : 'Upload GIF'}
                    </>
                  )}
                </label>
                <input
                  id={`upload-${slot.id}`}
                  type="file"
                  accept="image/gif,image/png,image/jpeg,image/webp,video/mp4,video/webm"
                  className="hidden"
                  disabled={uploadingId === slot.id}
                  onChange={(e) => handleUpload(slot.id, e.target.files?.[0])}
                  data-testid={`flyer-gif-input-${slot.id}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlyerGifsUpload;
